// NOTE: removed createApp usage for popovers; we render VNodes with Nuxt appContext instead.
// createApp/render exist at runtime; project vue types only re-export runtime-core
// @ts-expect-error - vue package exports createApp and render; types omit them
import { createApp, type Component } from 'vue'

import { useGlobal } from '@core/composables/useGlobal'

import { CalendarDateIcon, GraduationHat02Icon, Hash01Icon } from '@gohighlevel/ghl-icons/24/outline'

const iconHtmlCache = new Map<Component, string>()

function getIconHtml(IconComponent: Component): string {
  const cached = iconHtmlCache.get(IconComponent)
  if (cached) return cached
  if (typeof document === 'undefined') {
    return '<span class="flex-shrink-0"></span>'
  }
  const container = document.createElement('div')
  const app = createApp(IconComponent as Component)
  app.mount(container)
  const html = container.innerHTML
  app.unmount()
  iconHtmlCache.set(IconComponent, html)
  return html
}

function wrapFlexShrinkIcon(innerHtml: string): string {
  return `<span class="flex-shrink-0">${innerHtml}</span>`
}

function isEmojiIcon(icon?: string): boolean {
  if (!icon) return false
  const emojiRanges =
    /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F100}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u
  return emojiRanges.test(icon.trim()) && icon.length <= 8
}

function getChannelMentionIconHtml(icon?: string): string {
  if (icon && isEmojiIcon(icon)) return wrapFlexShrinkIcon(icon)
  return wrapFlexShrinkIcon(getIconHtml(Hash01Icon))
}

function getMentionIcon(type: string, icon?: string): string {
  if (type === 'channel-mention') return getChannelMentionIconHtml(icon)
  if (type === 'events-mention') return getIconHtml(CalendarDateIcon)
  if (type === 'courses-mention') return getIconHtml(GraduationHat02Icon)
  if (type === 'mention') return ''
  return wrapFlexShrinkIcon(getIconHtml(Hash01Icon))
}

export function cleanMentionText(text: string): string {
  if (!text) return ''
  let cleaned = text.trim()
  cleaned = cleaned.replace(/^([#@]|📅|📺)\s*/u, '').trim()
  return cleaned
}

export function getChannelMentionIcon(icon?: string): string {
  return getMentionIcon('channel-mention', icon)
}

const eventHandlers = new WeakMap<Element, (e: Event) => void>()

function handleMentionLinkClick(e: Event): void {
  // Safety net only: block native navigation for plain left-clicks so the chip href
  // never full-reloads. Must NOT stopPropagation — MentionableHtml's root handler
  // receives the bubbled click and performs the SPA navigation. Modified clicks
  // (Cmd/Ctrl/Shift/Alt) and non-primary buttons keep native behavior (new tab).
  const mouseEvent = e as MouseEvent
  if (mouseEvent.metaKey || mouseEvent.ctrlKey || mouseEvent.shiftKey || mouseEvent.altKey || mouseEvent.button !== 0) return
  e.preventDefault()
}

interface MentionChipData {
  dataType: string
  mentionType: string
  label: string
  name: string
  slug: string
  icon: string
  id: string
}

export interface MentionChipRenderOptions {
  groupSlug?: string
}

interface MentionChipContext {
  groupSlug?: string
  communitiesPrefix: string
  isGokollab: boolean
  processedLinks: Element[]
}

const getMentionChipData = (link: Element): MentionChipData => ({
  dataType: link.getAttribute('data-type') || '',
  mentionType: link.getAttribute('data-mention-type') || '',
  label: link.getAttribute('data-label') || '',
  name: link.getAttribute('data-name') || '',
  slug: link.getAttribute('data-slug') || '',
  icon: link.getAttribute('data-icon') || '',
  id: link.getAttribute('data-id') || '',
})

const renderMentionChipContent = (link: Element, data: MentionChipData): void => {
  const iconHtml = getMentionIcon(data.dataType, data.icon)
  const displayText = data.label || data.name || data.slug
  const cleanedText = cleanMentionText(displayText)
  const mentionText = data.dataType === 'mention' && cleanedText ? `@${cleanedText}` : cleanedText

  if (!mentionText) return

  const nodes: Node[] = []

  if (iconHtml) {
    const icon = document.createElement('span')
    icon.className = 'mention-icon'
    icon.innerHTML = iconHtml
    nodes.push(icon)
  }

  const text = document.createElement('span')
  text.className = 'mention-text truncate min-w-0'
  text.append(document.createTextNode(mentionText))
  nodes.push(text)

  link.replaceChildren(...nodes)
}

const getUserMentionHref = (data: MentionChipData, context: MentionChipContext): string | undefined => {
  if (!data.slug) return undefined
  return `${context.communitiesPrefix}/users/${data.slug}`
}

const getChannelMentionHref = (data: MentionChipData, context: MentionChipContext): string | undefined => {
  if (!data.slug || !context.groupSlug) return undefined
  return context.isGokollab
    ? `/${context.groupSlug}/channels/${data.slug}`
    : `/communities/groups/${context.groupSlug}/channels/${data.slug}`
}

const getCourseMentionHref = (data: MentionChipData, context: MentionChipContext): string | undefined => {
  if (!data.id || !context.groupSlug) return undefined
  return context.isGokollab
    ? `/${context.groupSlug}/learning?productId=${data.id}`
    : `/communities/groups/${context.groupSlug}/learning?productId=${data.id}`
}

const getEventMentionHref = (data: MentionChipData, context: MentionChipContext): string | undefined => {
  if (!data.id || !context.groupSlug) return undefined
  return context.isGokollab
    ? `/${context.groupSlug}/events?eventId=${data.id}`
    : `/communities/groups/${context.groupSlug}/events?eventId=${data.id}`
}

const getMentionChipHref = (data: MentionChipData, context: MentionChipContext): string | undefined => {
  if (data.dataType === 'mention') return getUserMentionHref(data, context)
  if (data.dataType === 'channel-mention') return getChannelMentionHref(data, context)
  if (data.dataType === 'courses-mention') return getCourseMentionHref(data, context)
  if (data.dataType === 'events-mention') return getEventMentionHref(data, context)
  return undefined
}

const setMentionChipHref = (link: Element, data: MentionChipData, context: MentionChipContext): void => {
  const href = getMentionChipHref(data, context)
  if (href) (link as HTMLAnchorElement).href = href
}

const renderMentionAsChip = (link: Element, context: MentionChipContext): void => {
  link.classList.add('min-h-6')

  const data = getMentionChipData(link)
  renderMentionChipContent(link, data)

  if (data.mentionType === 'broadcast') return

  setMentionChipHref(link, data, context)
  const handler = handleMentionLinkClick
  eventHandlers.set(link, handler)
  link.addEventListener('click', handler)
  context.processedLinks.push(link)
}

/**
 * Process mention nodes in display HTML. Pass `groupSlug` from the caller so this
 * common util does not import the communities groups store.
 */
export const renderMentionsAsChips = (el: HTMLElement, options: MentionChipRenderOptions = {}): (() => void) => {
  const { isGokollab } = useGlobal()
  const communitiesPrefix = isGokollab ? '' : '/communities'
  const mentionLinks = el.querySelectorAll('a[data-type], span[data-type]')
  const processedLinks: Element[] = []
  const context: MentionChipContext = {
    groupSlug: options.groupSlug,
    communitiesPrefix,
    isGokollab,
    processedLinks,
  }

  mentionLinks.forEach(link => renderMentionAsChip(link, context))

  return () => {
    processedLinks.forEach(link => {
      const handler = eventHandlers.get(link)
      if (handler) {
        link.removeEventListener('click', handler)
        eventHandlers.delete(link)
      }
    })
  }
}
